import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchProjects, createProject, deleteProject, toggleProjectStar } from '../../store/slices/projectsSlice';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import api from '../../services/api';
import { validationMessages, isValidProjectKey } from '../../utils/validationMessages';
import './Projects.css';

const SortIcon = ({ field, sortField, sortDir }) => {
  const active = sortField === field;
  if (active && sortDir === 'asc') {
    return (
      <svg className="sort-icon sort-icon--active" viewBox="0 0 16 16" width="11" height="11" fill="currentColor">
        <path d="M8 4.5l4.5 6h-9L8 4.5z"/>
      </svg>
    );
  }
  if (active && sortDir === 'desc') {
    return (
      <svg className="sort-icon sort-icon--active" viewBox="0 0 16 16" width="11" height="11" fill="currentColor">
        <path d="M8 11.5L3.5 5.5h9L8 11.5z"/>
      </svg>
    );
  }
  return (
    <svg className="sort-icon" viewBox="0 0 16 16" width="11" height="11" fill="currentColor">
      <path d="M8 3.5l3 4H5l3-4zM8 12.5L5 8.5h6L8 12.5z"/>
    </svg>
  );
};

const Projects = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { projects, pagination, loading } = useSelector((state) => state.projects);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    workflow_id: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
    isLoading: false
  });

  const canCreateProject = ['super_admin', 'admin'].includes(user?.role);
  const canDeleteProject = ['super_admin', 'admin'].includes(user?.role);

  const handleToggleStar = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(toggleProjectStar({ projectId: project.id, isCurrentlyStarred: project.is_starred }));
  };

  // Fetch available workflows for project creation
  const fetchWorkflows = useCallback(async () => {
    if (!canCreateProject) return;
    setLoadingWorkflows(true);
    try {
      const response = await api.get('/workflows/');
      setWorkflows(response.data.items || []);
      // Set default workflow if available
      const defaultWorkflow = response.data.items?.find(w => w.is_default);
      if (defaultWorkflow && !formData.workflow_id) {
        setFormData(prev => ({ ...prev, workflow_id: defaultWorkflow.id.toString() }));
      }
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoadingWorkflows(false);
    }
  }, [canCreateProject]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    dispatch(fetchProjects({ page: currentPage, pageSize, search, status: statusFilter }));
  }, [dispatch, currentPage, pageSize, search, statusFilter]);

  useEffect(() => {
    if (showCreateModal) {
      fetchWorkflows();
    }
  }, [showCreateModal, fetchWorkflows]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'key':     aVal = a.key || '';            bVal = b.key || '';            break;
        case 'name':    aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); break;
        case 'tasks':   aVal = a.task_count || 0;      bVal = b.task_count || 0;      break;
        case 'members': aVal = a.member_count || 0;    bVal = b.member_count || 0;    break;
        case 'created': aVal = new Date(a.created_at || 0); bVal = new Date(b.created_at || 0); break;
        default:        return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [projects, sortField, sortDir]);

  const truncateDesc = (text, max = 55) => {
    if (!text) return null;
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!formData.name) errors.name = validationMessages.project.name.required;
    if (!formData.key) errors.key = validationMessages.project.key.required;
    else if (!isValidProjectKey(formData.key)) {
      errors.key = validationMessages.project.key.format;
    }
    if (!formData.workflow_id) errors.workflow_id = validationMessages.project.workflow.required;
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setCreating(true);
    try {
      const projectData = {
        ...formData,
        workflow_id: parseInt(formData.workflow_id, 10)
      };
      await dispatch(createProject(projectData)).unwrap();
      toast.success('Project created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', key: '', description: '', workflow_id: '' });
      setFormErrors({});
    } catch (error) {
      toast.error(error || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = (projectId, projectName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Project',
      message: `Are you sure you want to delete "${projectName}"? This will permanently delete all tasks, sprints, and data associated with this project. This action cannot be undone.`,
      confirmText: 'Delete Project',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteProject(projectId)).unwrap();
          toast.success('Project deleted successfully');
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
          setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          toast.error(error || 'Failed to delete project');
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { class: 'badge-success', label: 'Active' },
      on_hold: { class: 'badge-warning', label: 'On Hold' },
      completed: { class: 'badge-info', label: 'Completed' },
      archived: { class: 'badge-secondary', label: 'Archived' },
    };
    return statusConfig[status] || { class: 'badge-primary', label: status };
  };

  return (
    <div className="projects-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Manage your projects and teams</p>
        </div>
        {canCreateProject && (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z"/>
              </svg>
              Create Project
            </button>
          </div>
        )}
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filters-bar">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M9.965 11.026a5 5 0 111.06-1.06l2.755 2.754a.75.75 0 11-1.06 1.06l-2.755-2.754zM10.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" clipRule="evenodd"/>
            </svg>
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={handleSearch}
            />
          </div>
          <select
            className="form-select"
            style={{ marginBottom: '0px' }}
            value={statusFilter}
            onChange={handleStatusFilter}
          >
            <option value="">All Projects</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
          <div className="page-size-select">
            <span>Show</span>
            <select
              className="form-select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="projects-table-container">
            <table className="table projects-table">
              <thead>
                <tr>
                  <th style={{ width: 40, padding: '0 8px' }} />
                  <th>Key</th><th>Name</th><th>Description</th>
                  <th>Status</th><th>Tasks</th><th>Members</th>
                  <th>Created</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [36, 140, 180, 52, 24, 24, 64],
                  [44, 120, 200, 60, 24, 24, 64],
                  [40, 150, 160, 52, 24, 24, 64],
                  [36, 130, 190, 60, 24, 24, 64],
                  [48, 140, 170, 52, 24, 24, 64],
                ].map((cols, ri) => (
                  <tr key={ri}>
                    <td className="star-col" />
                    {cols.map((w, ci) => (
                      <td key={ci}>
                        <div className="sk sk-line" style={{ width: w }} />
                      </td>
                    ))}
                    <td><div className="sk sk-line" style={{ width: 52 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : projects.length > 0 ? (
          <div className="projects-table-container">
            <table className="table projects-table">
              <thead>
                <tr>
                  <th style={{ width: 40, padding: '0 8px' }} />
                  <th className="th-sort" onClick={() => handleSort('key')}>
                    <span>Key <SortIcon field="key" sortField={sortField} sortDir={sortDir} /></span>
                  </th>
                  <th className="th-sort" onClick={() => handleSort('name')}>
                    <span>Name <SortIcon field="name" sortField={sortField} sortDir={sortDir} /></span>
                  </th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className="th-sort" onClick={() => handleSort('tasks')}>
                    <span>Tasks <SortIcon field="tasks" sortField={sortField} sortDir={sortDir} /></span>
                  </th>
                  <th className="th-sort" onClick={() => handleSort('members')}>
                    <span>Members <SortIcon field="members" sortField={sortField} sortDir={sortDir} /></span>
                  </th>
                  <th className="th-sort" onClick={() => handleSort('created')}>
                    <span>Created <SortIcon field="created" sortField={sortField} sortDir={sortDir} /></span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => (
                  <tr key={project.id}>
                    <td className="star-col">
                      <button
                        className={`star-btn${project.is_starred ? ' starred' : ''}`}
                        onClick={(e) => handleToggleStar(e, project)}
                        title={project.is_starred ? 'Remove star' : 'Star project'}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24"
                             fill={project.is_starred ? 'currentColor' : 'none'}
                             stroke="currentColor" strokeWidth="2">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      </button>
                    </td>
                    <td>
                      <span className="project-key">{project.key}</span>
                    </td>
                    <td>
                      <Link to={`/projects/${project.id}`} className="project-link">
                        {project.name}
                      </Link>
                    </td>
                    <td>
                      {truncateDesc(project.description)
                        ? <span className="project-desc" title={project.description}>{truncateDesc(project.description)}</span>
                        : <span className="project-desc project-desc--empty">No description</span>
                      }
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(project.status).class}`}>
                        {getStatusBadge(project.status).label}
                      </span>
                    </td>
                    <td>{project.task_count || 0}</td>
                    <td>{project.member_count || 0}</td>
                    <td>{new Date(project.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="actions">
                        <Link 
                          to={`/projects/${project.id}/board`}
                          className="btn btn-sm btn-ghost"
                          title="View Board"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M2 4.5A2.5 2.5 0 014.5 2h3a.5.5 0 01.5.5v11a.5.5 0 01-.5.5h-3A2.5 2.5 0 012 11.5v-7zm5 0V13h4.5a2.5 2.5 0 002.5-2.5v-7A2.5 2.5 0 0011.5 1h-3a.5.5 0 00-.5.5v3z"/>
                          </svg>
                        </Link>
                        {canDeleteProject && (
                          <button 
                            className="btn btn-sm btn-ghost btn-danger-hover"
                            onClick={() => handleDeleteProject(project.id, project.name)}
                            title="Delete Project"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2H7a.5.5 0 00-.5.5V3h3v-.5A.5.5 0 009 2zm2 1v-.5a1.5 1.5 0 00-1.5-1.5h-3A1.5 1.5 0 005 2.5V3H1.5a.5.5 0 000 1h.538l.853 10.66A2 2 0 004.885 16h6.23a2 2 0 001.994-1.84l.853-10.66h.538a.5.5 0 000-1H11zm1.958 1H3.042l.846 10.58a1 1 0 00.997.92h6.23a1 1 0 00.997-.92L12.958 4z" clipRule="evenodd"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="projects-pagination">
            <span className="pagination-info">
              {pagination.totalPages > 1
                ? `${((pagination.page - 1) * pagination.pageSize) + 1}–${Math.min(pagination.page * pagination.pageSize, pagination.total)} of ${pagination.total} projects`
                : `${pagination.total} project${pagination.total !== 1 ? 's' : ''}`}
            </span>
            {pagination.totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M9.78 4.22a.75.75 0 010 1.06L7.06 8l2.72 2.72a.75.75 0 11-1.06 1.06L5.47 8.53a.75.75 0 010-1.06l3.25-3.25a.75.75 0 011.06 0z"/>
                  </svg>
                  Prev
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                    ) : (
                      <button
                        key={item}
                        className={`btn btn-sm ${item === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={currentPage >= pagination.totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M6.22 4.22a.75.75 0 011.06 0l3.25 3.25a.75.75 0 010 1.06L7.28 11.78a.75.75 0 11-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && projects.length === 0 && (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor" opacity="0.3">
              <path d="M6 14a4 4 0 014-4h8l4 4h12a4 4 0 014 4v16a4 4 0 01-4 4H10a4 4 0 01-4-4V14z"/>
            </svg>
            <p>No projects found</p>
            {canCreateProject && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First Project
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
      >
        <form onSubmit={handleCreateProject}>
          <div className="form-group">
            <label className="form-label">Project Name <span className="required">*</span></label>
            <input
              type="text"
              className={`form-input ${formErrors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter project name"
            />
            {formErrors.name && <span className="form-error">{formErrors.name}</span>}
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Key <span className="required">*</span></label>
            <input
              type="text"
              className={`form-input ${formErrors.key ? 'error' : ''}`}
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
              placeholder="e.g., PROJ"
              maxLength={10}
            />
            {formErrors.key && <span className="form-error">{formErrors.key}</span>}
            <small className="form-hint">Used for task IDs (e.g., PROJ1, PROJ2). Uppercase letters and numbers only.</small>
          </div>
          
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter project description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Workflow <span className="required">*</span></label>
            <select
              className={`form-select ${formErrors.workflow_id ? 'error' : ''}`}
              value={formData.workflow_id}
              onChange={(e) => setFormData({ ...formData, workflow_id: e.target.value })}
              disabled={loadingWorkflows}
              style={{ paddingTop: '0px', paddingBottom: '0px' }}
            >
              <option value="">Select a workflow...</option>
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name} {workflow.is_default ? '(Default)' : ''} - {workflow.status_count} statuses
                </option>
              ))}
            </select>
            {formErrors.workflow_id && <span className="form-error">{formErrors.workflow_id}</span>}
            <small className="form-hint">
              Determines board columns and task statuses for this project
            </small>
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              style={{ marginTop: '0px' }}
              className="btn btn-secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
      />
    </div>
  );
};

export default Projects;
